/**
 * The estate's two ways of sending a work out — a loan to any counterparty,
 * a consignment to a gallery or agent — and the rail beside the forms that
 * shows what is currently out.
 *
 * Pure. Drafts, loans and arrangements go in; refusals, request bodies,
 * confirmations and rail rows come out. The copy is `estate.constants.ts`;
 * the three reads and two writes, the active tab and the drafts are
 * `useLoansConsignController.ts`. The collector's own view of the same
 * consignment rows is `arrangements/arrangements.model.ts`.
 *
 * A confirmation is read off the loan the server returned, not off the
 * draft: a borrower on ONDO was asked and a bare venue was recorded, and the
 * sentence must not claim the wrong one — see `loanConfirmation`. The rail
 * puts live rows first, whatever their date.
 */

import type { Arrangement, Loan, ProposeLoanRequest, StudioWork } from "@ondo/shared";
import type { OfferConsignmentRequest } from "@ondo/shared";

import { parseMoneyMinor } from "../lib/money.ts";
import { LOANS_COPY } from "./estate.constants.ts";

/**
 * The two owner acts — lend, and consign — and the rail beside them.
 *
 * Both forms end in an RPC that resolves the counterparty's ONDO id INSIDE a
 * definer with one opaque refusal, so neither form verifies anything the
 * server would not. The rail is the union of two durable reads:
 * `ondo.owner_loans()` and the consignment half of `ondo.my_arrangements()` —
 * a "what is true" question answered from tables, never a feed.
 */

export interface RailRow {
  key: string;
  badge: "loan" | "consign";
  title: string;
  /** 'On loan to Tate Modern, London' / 'Consigned to Hauser & Wirth'. */
  line: string;
  /** 'Mar 2026 – Jun 2026', a purpose, or terms — whatever the row knows. */
  detail: string | null;
  /** 'Active', 'Proposed', 'Offered — not yet answered', 'Returned'… */
  state: string;
  /** True while the arrangement still binds — finished rows render quieter. */
  live: boolean;
  /**
   * The loan this row may SEND OUT, or null.
   *
   * Narrower than `live` and mirroring `ondo.mark_loan_out()`'s own `where`
   * clause: 'agreed' only. A proposal nobody has answered cannot be despatched,
   * and a loan already out has left. A consignment is never dispatchable here —
   * a gallery takes delivery under its own arrangement.
   *
   * The rail declines to offer a control the database would refuse, in the
   * register `atLeast` documents — "the database decides, always" — and the
   * refusal still renders when the two disagree.
   */
  dispatchableLoanId: string | null;
}

export function toRail(loans: Loan[], arrangements: Arrangement[]): RailRow[] {
  const loanRows = loans.map(toLoanRow);
  const consignRows = arrangements
    .filter((arrangement) => arrangement.kind === "consignment")
    .map(toConsignRow);

  // Live first, then newest — an active loan matters more than last year's.
  return [...loanRows, ...consignRows].sort((a, b) =>
    a.live === b.live ? 0 : a.live ? -1 : 1,
  );
}

function toLoanRow(loan: Loan): RailRow {
  const live = loan.status === "proposed" || loan.status === "agreed" || loan.status === "out";

  return {
    key: `loan:${loan.loanId}`,
    badge: "loan",
    title: loan.title,
    line: LOANS_COPY.onLoanTo(
      loan.borrowerName ? `${loan.venueText} · ${loan.borrowerOndoId ?? ""}` : loan.venueText,
    ),
    detail: [dateSpan(loan.startsOn, loan.endsOn), loan.purposeText]
      .filter(Boolean)
      .join(" · ") || null,
    state: LOANS_COPY.loanStates[loan.status],
    live,
    dispatchableLoanId: loan.status === "agreed" ? loan.loanId : null,
  };
}

function toConsignRow(arrangement: Arrangement): RailRow {
  return {
    key: `consign:${arrangement.arrangementId}`,
    badge: "consign",
    title: arrangement.title,
    line: LOANS_COPY.consignedTo(
      `${arrangement.counterpartyName} · ${arrangement.counterpartyOndoId}`,
    ),
    detail: null,
    state:
      arrangement.acceptedAt === null ? LOANS_COPY.offerPending : LOANS_COPY.offerHeld,
    live: true,
    dispatchableLoanId: null,
  };
}

/** 'Mar 2026 – Jun 2026', either half optional. */
export function dateSpan(startsOn: string | null, endsOn: string | null): string | null {
  const label = (iso: string) =>
    new Date(`${iso}T00:00:00Z`).toLocaleDateString("en-GB", {
      month: "short",
      year: "numeric",
      timeZone: "UTC",
    });

  if (startsOn && endsOn) return `${label(startsOn)} – ${label(endsOn)}`;
  if (startsOn) return `${LOANS_COPY.from} ${label(startsOn)}`;
  if (endsOn) return `${LOANS_COPY.until} ${label(endsOn)}`;

  return null;
}

// ── The loan form ──────────────────────────────────────────────────────────

export interface LoanDraft {
  workId: string;
  venue: string;
  borrower: string;
  startsOn: string;
  endsOn: string;
  purpose: string;
}

export const EMPTY_LOAN: LoanDraft = {
  workId: "",
  venue: "",
  borrower: "",
  startsOn: "",
  endsOn: "",
  purpose: "",
};

/** Why it cannot be sent, or null. Shape only; the server owns the rest. */
export function loanRefusal(draft: LoanDraft): string | null {
  if (draft.workId === "") return LOANS_COPY.needsWork;
  if (draft.venue.trim() === "") return LOANS_COPY.needsVenue;
  if (draft.startsOn && draft.endsOn && draft.endsOn < draft.startsOn) {
    return LOANS_COPY.datesBackwards;
  }

  return null;
}

export function toLoanRequest(draft: LoanDraft): ProposeLoanRequest {
  return {
    venueText: draft.venue.trim(),
    borrowerOndoId: draft.borrower.trim() === "" ? null : draft.borrower.trim().toUpperCase(),
    startsOn: draft.startsOn || null,
    endsOn: draft.endsOn || null,
    purposeText: draft.purpose.trim() === "" ? null : draft.purpose.trim(),
  };
}

/**
 * What the confirmation says, read off the returned status — a borrower on
 * ONDO was ASKED, a bare venue was RECORDED, and the sentence must not claim
 * the wrong one.
 */
export function loanConfirmation(loan: Loan): string {
  return loan.status === "proposed"
    ? LOANS_COPY.loanProposed(loan.title)
    : LOANS_COPY.loanRecorded(loan.title);
}

// ── The consign form ───────────────────────────────────────────────────────

export interface ConsignDraft {
  workId: string;
  ondoId: string;
  asking: string;
  currency: string;
  note: string;
}

export const EMPTY_CONSIGN: ConsignDraft = {
  workId: "",
  ondoId: "",
  asking: "",
  currency: "GBP",
  note: "",
};

export function consignRefusal(draft: ConsignDraft): string | null {
  if (draft.workId === "") return LOANS_COPY.needsWork;
  if (!/^ONDO-[A-Za-z0-9]{4,}$/i.test(draft.ondoId.trim())) return LOANS_COPY.needsOndoId;
  if (draft.asking.trim() !== "" && parseMoneyMinor(draft.asking) === null) {
    return LOANS_COPY.badAsking;
  }

  return null;
}

export function toConsignRequest(draft: ConsignDraft): OfferConsignmentRequest {
  const askingMinor = draft.asking.trim() === "" ? null : parseMoneyMinor(draft.asking);

  return {
    ondoId: draft.ondoId.trim().toUpperCase(),
    askingMinor,
    currency: askingMinor === null ? null : draft.currency.trim().toUpperCase(),
    note: draft.note.trim() === "" ? null : draft.note.trim(),
  };
}

/** '{title} — {name} · {year}', for the work picker. */
export function pickerLabel(work: StudioWork): string {
  return [work.title, "—", work.attributedName, work.year ? `· ${work.year}` : ""]
    .filter(Boolean)
    .join(" ");
}
